import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building, MapPin } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const empty = { name: "", address: "", notes: "" };

export default function Properties() {
  const { data: properties = [], isLoading } = useSWR("/properties", fetcher);
  const { data: units = [] } = useSWR("/units", fetcher);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const unitCounts = units.reduce((acc, u) => {
    if (u.property_id) acc[u.property_id] = (acc[u.property_id] || 0) + 1;
    return acc;
  }, {});

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/properties/${editing.id}`, form);
      else await api.post("/properties", form);
      toast.success(editing ? "Properti diperbarui" : "Properti ditambahkan");
      setOpen(false);
      mutate("/properties");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!confirm(`Hapus properti ${p.name}?`)) return;
    try {
      await api.delete(`/properties/${p.id}`);
      toast.success("Properti dihapus");
      mutate("/properties");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Data Master</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Properti</h1>
          <p className="text-sm text-muted-foreground mt-1">Kelompokkan unit berdasarkan properti / lokasi.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="add-property-button">
          <Plus className="h-4 w-4" /> Tambah Properti
        </Button>
      </header>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : properties.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
              <Building className="h-7 w-7" />
            </div>
            <div className="font-display text-lg font-medium">Belum ada properti</div>
            <p className="text-sm text-muted-foreground -mt-2">Tambah properti pertama untuk mengelola unit multi-lokasi.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {properties.map((p) => (
            <Card key={p.id} data-testid={`property-card-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center mb-3">
                      <Building className="h-5 w-5" />
                    </div>
                    <div className="font-display text-lg font-medium truncate">{p.name}</div>
                    {p.address && (
                      <div className="text-xs text-muted-foreground flex items-start gap-1 mt-1">
                        <MapPin className="h-3 w-3 mt-0.5 shrink-0" /> <span className="truncate">{p.address}</span>
                      </div>
                    )}
                    <Badge variant="outline" className="mt-3 rounded-full text-[10px] uppercase tracking-wider">
                      {unitCounts[p.id] || 0} unit
                    </Badge>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-property-${p.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(p)} data-testid={`delete-property-${p.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Properti" : "Tambah Properti"}</DialogTitle>
            <DialogDescription>Gunakan untuk memisahkan unit di gedung/lokasi berbeda.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nama Properti</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="property-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Alamat</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Catatan</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="property-save-button">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
